import { useEffect, useState, type FormEvent } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import {
  DEFAULT_COUNTRY,
  PROPERTY_TYPES,
  normaliseTypes,
  propertyTypeSummary,
} from "@rentorng/core";
import { api } from "@/lib/api";
import { formValues, nullIfBlank, useApiMutation } from "@/lib/mutations";
import {
  resolveCountry,
  resolveParty,
  resolveStaff,
  searchCountryOptions,
  searchParties,
  searchStaff,
} from "@/lib/pickers";
import { SearchSelect } from "@/components/search-select";
import {
  PropertyTypeIcon,
  isResidentialType,
} from "@/components/property-icons";
import { Refusal } from "@/components/padlock";
import { titleCase } from "@/lib/format";
import { useSession } from "@/lib/session";
import {
  Button,
  Card,
  Field,
  Input,
  PageHeader,
  Select,
  Spinner,
  Textarea,
} from "@/components/ui";

/**
 * Create and edit share one component, so the two forms can never drift apart — which is how a
 * field ends up saveable on creation but silently un-editable afterwards.
 */
/**
 * Choosing the type.
 *
 * A dropdown of fourteen near-synonymous phrases is the slowest control on this form, and it
 * hides the mark the agent will actually be looking for afterwards. Tiles show all fourteen at
 * once, in the same colours the lists use, so the choice and the consequence are the same picture.
 */
function TypePicker({
  value,
  onChange,
}: {
  value: string[];
  onChange: (next: string[]) => void;
}) {
  const toggle = (type: string) => {
    // Never leave it with nothing: a property with no type has no mark and no place in a filter.
    if (value.includes(type)) {
      const next = value.filter((entry) => entry !== type);
      onChange(next.length ? next : value);
      return;
    }
    onChange([...value, type]);
  };

  return (
    <>
      {/*
        Grouped, because the colour was doing work nothing on the screen explained.
        Green marks are residential and blue ones are commercial — true before, and invisible:
        the tiles were in one grid, so the two colours read as decoration applied at random.
        A heading over each group makes the colour mean something without a legend.
      */}
      {[
        { heading: "Residential", residential: true },
        { heading: "Commercial and land", residential: false },
      ].map((group) => (
      <div key={group.heading} className="mb-3 last:mb-0">
      <p className="eyebrow mb-1.5">{group.heading}</p>
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
        {PROPERTY_TYPES.filter((type) => type.residential === group.residential).map((type) => {
          const selected = value.includes(type.value);
          return (
            <button
              key={type.value}
              type="button"
              aria-pressed={selected}
              onClick={() => toggle(type.value)}
              className={`flex flex-col items-center justify-center gap-2 rounded-[var(--radius-control)] border px-2 py-4 text-center text-[13px] leading-tight transition-colors ${
                selected
                  ? "border-brand-500 bg-brand-50 font-semibold text-brand-800"
                  : "border-ink-200 bg-white text-ink-700 hover:border-ink-300 hover:bg-ink-50"
              }`}
            >
              <PropertyTypeIcon
                type={type.value}
                size={26}
                className={`shrink-0 ${
                  selected
                    ? "text-brand-700"
                    : isResidentialType(type.value)
                    ? "text-brand-600"
                    : "text-info-600"
                }`}
              />
              <span className="min-w-0">{type.label}</span>
            </button>
          );
        })}
      </div>
      </div>
      ))}

      {/* Says the set back in words. Eight highlighted tiles are hard to read at a glance; "Block
          of flats and shop / retail" is not. The first one chosen leads, and draws the mark. */}
      {value.length > 1 ? (
        <p className="mt-2 text-xs text-ink-500">
          {propertyTypeSummary(value)} — the first is what draws its mark in a
          list.
        </p>
      ) : null}
    </>
  );
}

export function PropertyFormPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { vocab, org } = useSession();
  const editing = Boolean(id);
  const [landlordId, setLandlordId] = useState("");
  const [types, setTypes] = useState<string[]>(["block_of_flats"]);
  const [managerId, setManagerId] = useState("");
  const [country, setCountry] = useState(DEFAULT_COUNTRY);

  const { data: existing, isLoading } = useQuery({
    queryKey: ["property", id],
    queryFn: () =>
      api.get<{
        property: Record<string, never>;
        landlordName: string | null;
        managerName: string | null;
      }>(`/properties/${id}`),
    enabled: editing,
  });

  const { data: lookups } = useQuery({
    queryKey: ["property-lookups"],
    queryFn: async () => {
      const [landlords, staff] = await Promise.all([
        api.get<{ parties: Array<{ id: string; name: string }> }>(
          "/parties/lookup",
          { role: "landlord" },
        ),
        api.get<{ staff: Array<{ id: string; name: string }> }>(
          "/properties/lookup",
        ),
      ]);
      return { landlords: landlords.parties, staff: staff.staff };
    },
  });

  const save = useApiMutation({
    mutationFn: (input: Record<string, unknown>) =>
      editing
        ? api.patch<{ id: string }>(`/properties/${id}`, input)
        : api.post<{ id: string }>("/properties", input),
    invalidates: [["properties"], ["property", id ?? ""], ["dashboard"]],
    /*
      A new property goes straight to its first unit.

      It used to offer to create one inline — three fields on a form that is already long, and a
      unit that could only ever be the simplest kind. A property with no units is not much use to
      anybody, so the form hands over to the one that adds them properly, with the offers, the
      amenities and the listing switch on it. Editing stays where it was.
    */
    onSuccess: (data) =>
      navigate(editing ? `/app/properties/${id}` : `/app/properties/${data.id}/units/new`),
  });

  const property = (existing?.property ?? {}) as Record<
    string,
    string | number | null
  >;

  // Seed the pickers once the record arrives, so editing opens on the current choice rather
  // than on "Not set".
  useEffect(() => {
    if (!existing?.property) return;
    setLandlordId(String(property.landlordPartyId ?? ""));
    setTypes(normaliseTypes(property.types as unknown as string[]));
    setCountry(String(property.country ?? DEFAULT_COUNTRY));
    setManagerId(String(property.managerUserId ?? ""));
  }, [existing, property.landlordPartyId, property.managerUserId]);

  if (editing && isLoading) return <Spinner />;

  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const values = formValues(event.currentTarget);
    save.mutate({
      name: values.name,
      types,
      addressLine: nullIfBlank(values.addressLine),
      city: nullIfBlank(values.city),
      state: nullIfBlank(values.state),
      country: nullIfBlank(country),
      landlordPartyId: nullIfBlank(values.landlordPartyId),
      managerUserId: nullIfBlank(values.managerUserId),
      notes: nullIfBlank(values.notes),
    });
  };

  return (
    <div className="max-w-5xl">
      <PageHeader
        title={
          editing
            ? `Edit ${property.name ?? vocab.property}`
            : `Add a ${vocab.property}`
        }
        subtitle={
          editing
            ? undefined
            : `You will add ${vocab.units} to it next — a single house is still a ${vocab.property} with one ${vocab.unit}.`
        }
        back={
          editing
            ? {
                to: `/app/properties/${id}`,
                label: String(property.name ?? "Back"),
              }
            : { to: "/app/properties", label: titleCase(vocab.properties) }
        }
      />

      <Card>
        <form onSubmit={submit} className="space-y-6">
          {/* A save refused because of the plan — editing a building above what the plan opens —
              comes back carrying the gate the server built, and is drawn as the padlock and the way
              out of it rather than as a red box. Everything else is the red box it always was. */}
          <Refusal error={save.error} message={save.errorMessage} />

          {/* Two columns, split by the question each half answers: what the building is, and
              where it is and whose it is. On one column the order is unchanged. */}
          <div className="grid gap-x-8 gap-y-6 lg:grid-cols-2">
            <div className="space-y-4">
              <Field
                label={`${titleCase(vocab.property)} name`}
                htmlFor="name"
                required
                error={save.fieldErrors.name}
              >
                <Input
                  id="name"
                  name="name"
                  required
                  defaultValue={String(property.name ?? "")}
                  placeholder="Palm Court"
                />
              </Field>

              <Field
                label="Type"
                group
                hint={`Select multiple if the buildings in this ${vocab.property} are more than one type.`}
              >
                <TypePicker value={types} onChange={setTypes} />
              </Field>

              {/*
                A commission rate stood here and is gone with the model behind it.

                The agency's income is not a share of the rent collected on this building: the rent
                is the owner's, whole, and the agency is paid by the tenant on a line of its own.
                Which charges a letting carries, and whose each of them is, is answered on the unit
                and on the tenancy — where the money actually is — rather than as a percentage set
                once on a property. See ADR-0131.
              */}
            </div>

            <div className="space-y-4">
              <Field label="Address" htmlFor="addressLine">
                <Input
                  id="addressLine"
                  name="addressLine"
                  defaultValue={String(property.addressLine ?? "")}
                  placeholder="14 Bourdillon Road"
                />
              </Field>

              <div className="grid gap-4 sm:grid-cols-2">
                <Field label="City / area" htmlFor="city">
                  <Input
                    id="city"
                    name="city"
                    defaultValue={String(property.city ?? "")}
                    placeholder="Ikoyi"
                  />
                </Field>
                <Field label="State / region" htmlFor="state">
                  <Input
                    id="state"
                    name="state"
                    defaultValue={String(property.state ?? "")}
                    placeholder="Lagos"
                  />
                </Field>
              </div>

              <Field label="Country" htmlFor="country">
                {/* Free text here meant the same country arrived as three spellings and no report
                  ever grouped them. The list is fixed; the default lives in the data. */}
                <SearchSelect
                  id="country"
                  name="country"
                  value={country}
                  onChange={(next) => setCountry(next)}
                  search={searchCountryOptions}
                  resolve={resolveCountry}
                  placeholder="Choose a country"
                  emptyMessage="No country matched."
                />
              </Field>

              <div className="grid gap-4 sm:grid-cols-2">
                <Field
                  label={titleCase(vocab.landlord)}
                  htmlFor="landlordPartyId"
                >
                  <SearchSelect
                    id="landlordPartyId"
                    name="landlordPartyId"
                    value={landlordId}
                    onChange={(next) => setLandlordId(next)}
                    search={searchParties("landlord")}
                    resolve={resolveParty}
                    selectedOption={
                      existing?.landlordName && property.landlordPartyId
                        ? {
                            value: String(property.landlordPartyId),
                            label: existing.landlordName,
                          }
                        : undefined
                    }
                    placeholder="Not set"
                    emptyMessage={`No ${vocab.landlord} matched.`}
                  />
                </Field>

                <Field
                  label="Managed by"
                  htmlFor="managerUserId"
                  hint="Agents see only the properties assigned to them."
                >
                  <SearchSelect
                    id="managerUserId"
                    name="managerUserId"
                    value={managerId}
                    onChange={(next) => setManagerId(next)}
                    search={searchStaff}
                    resolve={resolveStaff}
                    selectedOption={
                      existing?.managerName && property.managerUserId
                        ? {
                            value: String(property.managerUserId),
                            label: existing.managerName,
                          }
                        : undefined
                    }
                    placeholder="Anyone"
                    emptyMessage="Nobody matched."
                  />
                </Field>
              </div>

              <Field label="Notes" htmlFor="notes">
                <Textarea
                  id="notes"
                  name="notes"
                  placeholder="Anything worth remembering about this property."
                  defaultValue={String(property.notes ?? "")}
                  rows={3}
                />
              </Field>
            </div>
          </div>

          <div className="flex gap-2 border-t border-ink-100 pt-4">
            <Button type="submit" disabled={save.isPending}>
              {save.isPending
                ? "Saving…"
                : editing
                ? "Save changes"
                : `Save ${vocab.property}`}
            </Button>
            <Button
              variant="secondary"
              onClick={() =>
                navigate(editing ? `/app/properties/${id}` : "/app/properties")
              }
            >
              Cancel
            </Button>
          </div>
        </form>
      </Card>
    </div>
  );
}
